/* 26 Sep 2026: one validated add request; installation and care are explicit form choices. */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    static busy = false;
    static needsReview = false;
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton?.querySelector('span');
      if (!this.form || !this.submitButton || !this.variantIdInput) return;
      // Do not clear Shopify's disabled state for an unavailable variant.
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.hideErrors = this.dataset.hideErrors === 'true';
      this.querySelectorAll('[data-ps-installation], [data-ps-service-plan], [data-ps-service-consent]')
        .forEach((input) => { input.disabled = false; });
      this.querySelector('[data-ps-service-plan]')?.addEventListener('change', () => this.updateConsent());
      this.updateConsent();
    }
    updateConsent() {
      const choice = this.querySelector('[data-ps-service-plan]');
      const consent = this.querySelector('[data-ps-service-consent]');
      const wrap = this.querySelector('[data-ps-service-consent-wrap]');
      if (!consent || !wrap) return;
      wrap.hidden = !choice?.value;
      consent.required = Boolean(choice?.value);
      consent.checked = false;
    }
    async onSubmitHandler(event) {
      event.preventDefault();
      if (ProductForm.busy || this.isSubmitting || this.submitButton.disabled || this.variantIdInput.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true' || window.PSCheckoutCartBusy) return;
      if (ProductForm.needsReview) { this.reviewError('Please review the cart before adding again.'); return; }
      this.handleErrorMessage();
      let fd, id, quantity, plan, extras = [];
      try {
        fd = new FormData(this.form);
        id = fd.get('id');
        quantity = Number(fd.has('quantity') ? fd.get('quantity') : 1);
        const quantityInput = this.form.elements.namedItem('quantity');
        if (!/^[1-9]\d*$/.test(String(id || ''))) throw Error('Select an available product option.');
        if (!Number.isSafeInteger(quantity) || quantity < 1 ||
            (quantityInput?.checkValidity && !quantityInput.checkValidity())) {
          quantityInput?.reportValidity?.();
          throw Error('Choose a valid whole-number quantity before adding to cart.');
        }
        if (!this.form.reportValidity()) return;
        const plans = [...new Set(fd.getAll('selling_plan').filter(Boolean))];
        if (plans.length > 1) throw Error('Choose one subscription plan.');
        plan = plans[0] || '';
        if (this.dataset.requiresSellingPlan === 'true' && !plan) throw Error('Choose a service plan first.');
        if (plan && !/^[1-9]\d*$/.test(plan)) throw Error('Choose a valid service plan.');
        const install = this.querySelector('[data-ps-installation]');
        if (install?.checked && !install.disabled) extras.push({ id: install.value, quantity });
        const service = this.querySelector('[data-ps-service-plan]');
        if (service?.value && !service.disabled) {
          if (!this.querySelector('[data-ps-service-consent]')?.checked) throw Error('Confirm the annual service-plan billing before adding it.');
          if (![...service.options].some((option) => option.value === service.value)) throw Error('Choose an available servicing plan.');
          extras.push({ id: service.dataset.variantId, quantity, selling_plan: service.value });
        }
        if (extras.some((extra) => !/^[1-9]\d*$/.test(String(extra.id || '')) || String(extra.id) === String(id))) {
          throw Error('The selected add-on is unavailable. Contact us for help.');
        }
      } catch (error) { this.handleErrorMessage(error.message); return; }

      ProductForm.busy = true; this.isSubmitting = true; window.PSCheckoutProductBusy = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      const spinner = this.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden');
      let sent = false, confirmed = false, timer;
      const locked = new Map();
      const inert = new Map();
      document.querySelectorAll('product-form [type="submit"], product-form [data-ps-installation], product-form [data-ps-service-plan], product-form [data-ps-service-consent], button[name="checkout"]')
        .forEach((control) => { locked.set(control, control.disabled); control.disabled = true; });
      document.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout, .additional-checkout-buttons').forEach((node) => {
        inert.set(node, node.inert); node.inert = true;
      });
      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        fd.delete('installation_addon'); fd.delete('service_addon_plan');
        let sections = [];
        if (!extras.length && this.cart) {
          try {
            sections = this.cart.getSectionsToRender().map((section) => section.id);
            this.cart.setActiveElement(document.activeElement);
          } catch (_) { sections = []; }
        }
        if (sections.length) { fd.set('sections', sections.join(',')); fd.set('sections_url', window.location.pathname); }
        config.body = fd;
        let group = '';
        if (extras.length) {
          const properties = {};
          for (const [key, value] of fd.entries()) {
            if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
            if (typeof value !== 'string') {
              if (value.size) throw Error('Please contact us to combine uploaded files with installation. Nothing has been submitted.');
              continue;
            }
            properties[key.slice(11, -1)] = value;
          }
          group = this.selectionId();
          properties._ps_checkout_selection = group;
          const parent = { id, quantity, properties };
          if (plan) parent.selling_plan = plan;
          extras = extras.map((extra) => ({ ...extra, parent_id: id, properties: { _ps_checkout_selection: group } }));
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({ items: [parent, ...extras] });
        }
        const abort = new AbortController();
        timer = setTimeout(() => abort.abort(), 20000);
        config.signal = abort.signal;
        sent = true;
        const http = await fetch(routes.cart_add_url, config);
        const result = await http.json();
        clearTimeout(timer);
        if (!http.ok || result.status || result.errors) {
          this.publishCartEvent(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: id,
            errors: result.errors || result.description, message: result.message });
          throw Error(typeof result.description === 'string' ? result.description : 'The selected items could not be confirmed.');
        }
        if (extras.length) {
          // Verify every selected line and relationship in Shopify's returned cart, not the popup state.
          const check = new AbortController();
          timer = setTimeout(() => check.abort(), 15000);
          const cartResponse = await fetch(`${routes.cart_url.replace(/\/$/, '')}.js`, { cache: 'no-store', signal: check.signal });
          const state = await cartResponse.json(); clearTimeout(timer);
          if (!cartResponse.ok || !Array.isArray(state.items)) throw Error('The updated cart could not be confirmed.');
          const lines = state.items.filter((line) => line.properties?._ps_checkout_selection === group);
          const parents = lines.filter((line) => String(line.variant_id) === String(id) && !line.parent_relationship);
          if (parents.length !== 1 || parents[0].quantity !== quantity || String(parents[0].selling_plan_allocation?.selling_plan?.id || '') !== String(plan) || lines.length !== extras.length + 1) {
            throw Error('Please review the equipment and extras in your cart.');
          }
          for (const extra of extras) {
            const children = lines.filter((line) => String(line.variant_id) === String(extra.id));
            if (children.length !== 1 || children[0].quantity !== quantity || children[0].parent_relationship?.parent_key !== parents[0].key ||
                String(children[0].selling_plan_allocation?.selling_plan?.id || '') !== String(extra.selling_plan || '')) {
              throw Error('Please review the selected installation or servicing in your cart.');
            }
          }
          confirmed = true;
          this.publishCartEvent(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: id, cartData: state });
          this.navigateToCart(); return;
        }
        const added = result.id ? result : result.items?.[0];
        if (String(added?.id) !== String(id) || !(added.quantity > 0)) throw Error('The cart response could not be confirmed.');
        confirmed = true;
        this.publishCartEvent(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: id, cartData: result });
        if (!this.cart || !sections.length || sections.some((section) => !result.sections?.[section])) {
          this.navigateToCart(); return;
        }
        const renderCart = () => {
          try { this.cart.classList.remove('is-empty'); this.cart.renderContents(result); }
          catch (_) { this.navigateToCart(); }
        };
        const quickAdd = this.closest('quick-add-modal');
        if (quickAdd) {
          document.body.addEventListener('modalClosed', () => setTimeout(renderCart), { once: true });
          try { quickAdd.hide(true); } catch (_) { this.navigateToCart(); }
        } else renderCart();
      } catch (error) {
        if (sent && !confirmed) {
          ProductForm.needsReview = true;
          this.reviewError(`${error.message || 'The connection was interrupted.'} Review the cart before trying again; we have not repeated the request.`);
        } else if (confirmed) this.navigateToCart();
        else this.handleErrorMessage(error.message || 'Please review your selection.');
      } finally {
        clearTimeout(timer);
        locked.forEach((wasDisabled, control) => {
          if (!control.isConnected) return;
          const owner = control.closest('product-form');
          control.disabled = control === owner?.submitButton && typeof owner.variantDisabled === 'boolean' ? owner.variantDisabled : wasDisabled;
        });
        inert.forEach((wasInert, node) => { if (node.isConnected) node.inert = wasInert; });
        ProductForm.busy = false; this.isSubmitting = false; window.PSCheckoutProductBusy = false;
        this.submitButton.removeAttribute('aria-disabled'); this.submitButton.classList.remove('loading');
        spinner?.classList.add('hidden');
        try { if (typeof CartPerformance !== 'undefined') CartPerformance.measureFromEvent('add:user-action', event); } catch (_) {}
      }
    }
    selectionId() {
      if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
      if (typeof globalThis.crypto?.getRandomValues === 'function') return Array.from(globalThis.crypto.getRandomValues(new Uint32Array(4)), (v) => v.toString(16)).join('-');
      ProductForm.sequence = (ProductForm.sequence || 0) + 1;
      return `${Date.now().toString(36)}-${ProductForm.sequence}-${Math.random().toString(36).slice(2)}`;
    }
    publishCartEvent(name, data) {
      try { Promise.resolve(publish(name, data)).catch(() => {}); } catch (_) {}
    }
    navigateToCart() { window.location.assign(window.routes.cart_url); }
    reviewError(message) {
      this.handleErrorMessage(message);
      if (this.errorMessage) {
        const link = document.createElement('a'); link.href = routes.cart_url; link.textContent = 'Review cart';
        this.errorMessage.append(' ', link);
      }
    }
    handleErrorMessage(message = false) {
      this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessageWrapper?.querySelector('.product-form__error-message');
      if (!this.errorMessage) return;
      this.errorMessageWrapper.hidden = !message;
      this.errorMessage.textContent = message || '';
    }
    toggleSubmitButton(disable = true, text) {
      this.variantDisabled = disable;
      this.variantIdInput.disabled = disable;
      this.submitButton.disabled = disable || this.isSubmitting || ProductForm.busy;
      if (text && disable) this.submitButtonText.textContent = text;
      else if (!disable) {
        this.submitButtonText.textContent = window.variantStrings.addToCart;
        this.submitButtonText.classList.remove('hidden');
        this.submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
      }
    }
    get variantIdInput() { return this.form?.querySelector('[name="id"]'); }
  });
  // Covers checkout buttons in an existing notification while another add is pending.
  const writing = () => customElements.get('product-form').busy;
  document.addEventListener('click', (event) => {
    if (writing() && event.target.closest?.('button[name="checkout"], a[href="/checkout"], .shopify-payment-button, shopify-accelerated-checkout')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', (event) => {
    if (writing() && !event.target.closest('product-form') && event.target.querySelector('[name="checkout"]')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
}
